/**
 * @fileoverview Componente Organismo para agrupar y mostrar respuestas rápidas por equipo en un acordeón, con soporte para edición y eliminación de equipos.
 * Cumple con Atomic Design (Organism) y optimizaciones de renderizado (React.memo, useCallback, useMemo).
 *
 * @module FastAnswerTeamGroupMC
 * @category Component - Organism
 * @since 2.0.0
 * @version 3.1.0
 */

import React, {
  memo,
  useMemo,
  useCallback,
  useState,
  useEffect
} from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Card from 'react-bootstrap/Card';

import FastAnswerMC from './FastAnswerMC';
import ContextAwareToggleMC from './ContextAwareToggleMC';

/**
 * @typedef {Object} FastAnswerTeam
 * @description Estructura de un equipo con respuestas rápidas.
 * @property {string|number} id - Identificador único del equipo.
 * @property {string} name - Nombre del equipo.
 * @property {Array<Object>} quick_answers - Respuestas rápidas asociadas al equipo.
 */

/**
 * @typedef {Object} FastAnswerTeamGroupMCProps
 * @description Props del componente FastAnswerTeamGroupMC.
 * @property {Array<FastAnswerTeam>} teams - Lista de equipos con respuestas rápidas.
 * @property {boolean} [fullButtons] - Si se muestran botones de acción completos.
 * @property {function} [editTeam] - Callback para editar un equipo.
 * @property {function} [removeTeam] - Callback para eliminar un equipo.
 * @property {boolean} [appliedFilters] - Indica si hay filtros aplicados.
 * @property {function} onSelectAnswer - Callback para seleccionar/copiar/enviar una respuesta rápida.
 * @property {boolean} [onMobile=false] - Indica si se está visualizando en móvil.
 */

/**
 * Componente Organismo para agrupar y mostrar respuestas rápidas por equipo en un acordeón.
 *
 * @component FastAnswerTeamGroupMC
 * @category Component - Organism
 *
 * @description
 * **Características:**
 * - Agrupa respuestas rápidas por equipo y las muestra en un acordeón expandible.
 * - Permite edición y eliminación de equipos si se proveen los callbacks.
 * - Renderiza cada respuesta rápida usando FastAnswerMC.
 * - Optimizado con React.memo, useCallback y useMemo.
 *
 * **Atomic Design:**
 * - **Nivel:** Organism
 * - **Composición:** Usa organismos (FastAnswerMC, ContextAwareToggleMC), componentes de Bootstrap (Accordion, Card)
 *
 * @param {FastAnswerTeamGroupMCProps} props - Propiedades del componente
 * @returns {JSX.Element|null} Elemento renderizado del grupo de equipos o null si no hay equipos
 *
 * @example
 * <FastAnswerTeamGroupMC
 *   teams={teams}
 *   fullButtons={true}
 *   appliedFilters={appliedFilters}
 *   onSelectAnswer={(answer, action) => {}}
 * />
 *
 * @see {@link FastAnswerMC} Renderizado de respuesta rápida
 * @see {@link ContextAwareToggleMC} Toggle de acordeón con acciones de equipo
 *
 * @since 2.0.0
 * @version 3.1.0 - Documentación JSDoc completa y optimización de performance
 */

const FastAnswerTeamGroupMC = memo(({
  teams,
  fullButtons,
  editTeam,
  removeTeam,
  appliedFilters,
  onSelectAnswer,
  onMobile = false
}) => {
  /**
   * Firma estable de los equipos mostrados (evita reset por nueva referencia de array sin cambio real).
   * @type {string}
   */
  const teamsLayoutSignature = useMemo(
    () => teams?.map((t) => `${t.id}:${t.quick_answers?.length ?? 0}`).join('|') ?? '',
    [teams]
  );

  const [accordionActiveKey, setAccordionActiveKey] = useState([]);

  /**
   * defaultActiveKey no se reaplica tras el primer montaje; con filtros/búsqueda hay que abrir paneles al cambiar resultados.
   */
  useEffect(() => {
    if (!appliedFilters || !teams?.length) {
      setAccordionActiveKey([]);
      return;
    }
    setAccordionActiveKey(teams.map((_, index) => `${index}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `teams` se usa al ejecutar; incluirlo reabriría paneles si el padre recrea la referencia del array en cada render
  }, [appliedFilters, teamsLayoutSignature]);

  /**
   * Callback memoizado para editar un equipo.
   * @function memoizedEditTeam
   * @memberof FastAnswerTeamGroupMC
   * @param {FastAnswerTeam} team - Equipo a editar
   */
  const memoizedEditTeam = useCallback((team) => {
    editTeam?.(team);
  }, [editTeam]);

  /**
   * Callback memoizado para eliminar un equipo.
   * @function memoizedRemoveTeam
   * @memberof FastAnswerTeamGroupMC
   * @param {FastAnswerTeam} team - Equipo a eliminar
   */
  const memoizedRemoveTeam = useCallback((team) => {
    removeTeam?.(team);
  }, [removeTeam]);

  /**
   * Renderiza un equipo y sus respuestas rápidas dentro del acordeón.
   * @function renderTeam
   * @memberof FastAnswerTeamGroupMC
   * @param {FastAnswerTeam} team - Equipo a renderizar
   * @param {number} index - Índice del equipo
   * @returns {JSX.Element} Card del equipo con respuestas rápidas
   */
  const renderTeam = useCallback((team, index) => {
    const eventKey = `${index}`;

    return (
      <Card key={`team-list-${team.id || index}`} bsPrefix="list-card">
        <Card.Header bsPrefix="list-header">
          <ContextAwareToggleMC
            eventKey={eventKey}
            team={team}
            editTeam={memoizedEditTeam}
            removeTeam={memoizedRemoveTeam}
          />
        </Card.Header>
        <Accordion.Collapse eventKey={eventKey}>
          <Card.Body bsPrefix="list-card-body" className="tw-pt-2">
            {team.quick_answers?.map((answer, qAIndex) => (
              <div key={`answer-${team.id || index}-${answer.id || qAIndex}`}>
                <FastAnswerMC
                  answer={answer}
                  onMobile={onMobile}
                  fullButtons={fullButtons}
                  onSelectAnswer={onSelectAnswer}
                />
              </div>
            )) || []}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    );
  }, [memoizedEditTeam, memoizedRemoveTeam, onMobile, fullButtons, onSelectAnswer]);

  // Early return si no hay teams
  if (!teams || teams.length === 0) {
    return null;
  }

  return (
    <div className="tw-wrapper">
      <Accordion
        bsPrefix="list-accordion"
        alwaysOpen
        activeKey={accordionActiveKey}
        onSelect={setAccordionActiveKey}
      >
        {teams.map(renderTeam)}
      </Accordion>
    </div>
  );
});

FastAnswerTeamGroupMC.displayName = 'FastAnswerTeamGroupMC';

export default FastAnswerTeamGroupMC;
